import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from "@mui/material";
import { useState, type FC } from "react";
import toast from "react-hot-toast";
import { useFlags } from "../../../../server/optimizely";
import { useSupabaseCallback } from "../../../../server/supabase/hooks";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { uuidv4, type UUID } from "../../../../utils/uuid";
import { isEmailChannel, type ChannelName } from "../../../channels/types";
import { EmailMomentLoader } from "../../../email-moment/server/email-moment-loader";
import type { EmailMoment } from "../../../email-moment/types";
import { $isDynamicContentNode } from "../../../message/editor/nodes/dynamic-content/dynamic-content-node";
import { $isDynamicContentToolbarNode } from "../../../message/editor/nodes/dynamic-content/helpers";
import { $isPollNode } from "../../../message/editor/nodes/poll/helpers";
import type { SerialisedLexicalEditorDesign } from "../../../message/editor/types/serialised-editor-design";
import { convertLexicalDesign } from "../../../moment/component/channel/utils/convert-lexical-design";
import { prettyChannelName } from "../../../channels/utils";

export const FollowUpMessageImport: FC<{
  id: UUID | null;
  channel: ChannelName;
  setDesign: (design: SerialisedLexicalEditorDesign) => void;
  useLexicalEditor?: boolean;
  onEmailMomentImport?: (emailMoment: EmailMoment) => void;
  onDynamicSegmentIdMapImport?: (map: Record<string, string> | null) => void;
}> = ({
  id,
  channel,
  setDesign,
  useLexicalEditor = true,
  onEmailMomentImport,
  onDynamicSegmentIdMapImport,
}) => {
  const dialog = useDialog();
  const [loading, setLoading] = useState(false);
  const {
    flags: { cha_4024_import_follow_up_content },
  } = useFlags("cha_4024_import_follow_up_content");

  const { handleClose } = dialog;

  const handleImport = useSupabaseCallback(
    async ({ supabase }) => {
      if (!id || !channel) return;

      setLoading(true);

      if (!useLexicalEditor && onEmailMomentImport) {
        // Newsletter import: load the original moment's email_moment and dynamic_segment_id_map
        const { data: momentData, error: momentError } = await supabase
          .from("moment")
          .select("email_moment_id, dynamic_segment_id_map")
          .eq("id", id)
          .single();

        if (momentError || !momentData?.email_moment_id) {
          console.error("Error importing newsletter message:", momentError);
          toast.error("Unable to import message");
          setLoading(false);
          handleClose();
          return;
        }

        const { data: loadedEmailMoment } = await new EmailMomentLoader(supabase).load(
          momentData.email_moment_id,
        );

        if (!loadedEmailMoment) {
          toast.error("Unable to import message");
          setLoading(false);
          handleClose();
          return;
        }

        // Assign a new id so the follow-up creates its own email_moment record
        // instead of overwriting the original moment's email_moment.
        onEmailMomentImport({
          ...loadedEmailMoment,
          id: uuidv4(),
        });

        // Import the dynamic_segment_id_map from the original moment
        if (onDynamicSegmentIdMapImport) {
          onDynamicSegmentIdMapImport(
            (momentData.dynamic_segment_id_map as Record<string, string>) ?? null,
          );
        }

        setLoading(false);
        toast.success("Content imported successfully");
        handleClose();
        return;
      }

      // Lexical import
      const { data, error } = await supabase
        .from("moment")
        .select("design,channel")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error importing message:", error);
        toast.error("Unable to import message");
        setLoading(false);
        return;
      }

      if (!data.channel) {
        console.error("Parent message has no channel selected");
        toast.error("Parent message has no channel selected");
        setLoading(false);
        handleClose();
        return;
      }

      if (!data.design) {
        console.error("Parent message has no design");
        toast.error("Parent message has no design");
        setLoading(false);
        handleClose();
        return;
      }

      setDesign(
        convertLexicalDesign(
          channel,
          data.channel,
          data.design as SerialisedLexicalEditorDesign,
          false,
          [],
          [$isDynamicContentNode, $isDynamicContentToolbarNode, $isPollNode],
        ),
      );

      setLoading(false);
      toast.success("Content imported successfully");
      handleClose();
    },
    [
      id,
      channel,
      handleClose,
      setDesign,
      useLexicalEditor,
      onEmailMomentImport,
      onDynamicSegmentIdMapImport,
    ],
  );

  if (!cha_4024_import_follow_up_content) return null;

  const changedOffNewsletterEditor = !useLexicalEditor && !isEmailChannel(channel);

  return (
    <>
      <Tooltip
        title={`Newsletter content cannot be imported for ${prettyChannelName(channel)}`}
        arrow
      >
        <span>
          <Button
            variant="outlined"
            color="primary"
            onClick={dialog.handleOpen}
            data-analytics-id="follow-up-message-import"
            disabled={!id || !channel || changedOffNewsletterEditor}
            loading={loading}
          >
            Import content from original Communication
          </Button>
        </span>
      </Tooltip>
      <Dialog open={dialog.open} onClose={dialog.handleClose}>
        <DialogTitle>Import content from original Communication</DialogTitle>
        <DialogContent>
          <p>This will replace any content you have added to the Follow-Up already.</p>
          <p>Remember to edit the text so it acts as a Follow-Up to the original Communication!</p>
          {useLexicalEditor && (
            <p>
              Any dynamic content in the original Communication will be removed. If you are using a
              different channel for the Follow-Up, we will try our best to import the content,
              however some formatting may be lost.
            </p>
          )}
          <p>
            Bear in mind, if the content of the original Communication changes, it won’t be updated
            in this Follow-Up.
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={dialog.handleClose}
            color="primary"
            variant="text"
            data-analytics-id="follow-up-message-import-cancel"
            loading={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleImport()}
            color="primary"
            variant="contained"
            data-analytics-id="follow-up-message-import-import"
            loading={loading}
          >
            Import content
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
